<template>
    <div class="container" v-if="initDone && store.federationMetadata">
        <div class="columns">
            <div class="column">
                <button class="button is-light is-small menu-toggle is-hidden-tablet mb-3" @click="openSidebarMenu">
                    <span class="icon">
                        <i class="fa fa-angle-double-right"></i>
                    </span>
                </button>

                <h1 class="title mb-2">
                    <span>{{ store.federationMetadata.federationName }}</span>
                </h1>
                <a :href="urls.federations().listing()">
                    <span class="icon">
                        <i class="fa fa-arrow-left" />
                    </span>
                    <span>{{ translate("federation.back_to_listing") }}</span>
                </a>
            </div>
        </div>

        <div class="columns">
            <div class="column is-narrow">
                <SidebarMenu ref="sidebarMenu">
                    <p class="menu-label">{{ translate("help_center.section.general") }}</p>
                    <ul class="menu-list">
                        <li>
                            <a
                                :class="{ 'is-active': activeSection === 'federationDetails' }"
                                @click="activateSection('federationDetails')"
                            >
                                {{ translate("federation.details") }}
                            </a>
                        </li>
                        <li>
                            <a
                                :class="{ 'is-active': activeSection === 'federationMembers' }"
                                @click="activateSection('federationMembers')"
                            >
                                {{ translate("federation.federation_members") }}
                            </a>
                        </li>
                        <li>
                            <a :class="{ 'is-active': activeSection === 'billing' }" @click="activateSection('billing')">
                                {{ translate("federation.billing_details") }}
                            </a>
                        </li>
                        <li>
                            <a :class="{ 'is-active': activeSection === 'rankings' }" @click="activateSection('rankings')">
                                {{ translate("federation.rankings") }}
                            </a>
                        </li>
                    </ul>
                </SidebarMenu>
            </div>

            <div class="column">
                <DetailsSection v-if="activeSection === 'federationDetails'" />
                <MembersSection v-else-if="activeSection === 'federationMembers'" />
                <BillingSection v-else-if="activeSection === 'billing'" />
                <RankingsSection v-else-if="activeSection === 'rankings'" />
            </div>
        </div>
    </div>
    <div class="container" v-else>
        <div class="columns is-mobile is-centered">
            <LoadingSpinner2 class="has-added-margin" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, watch } from "vue";
    import { handleError } from "../../Common/errorHandler";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import DetailsSection from "./DetailsSection.vue";
    import { FederationMenuSection, useFederationDashboardStore } from "./federationManagementStore";
    import MembersSection from "./MembersSection.vue";
    import BillingSection from "./BillingSection.vue";
    import RankingsSection from "./RankingsSection.vue";
    import SidebarMenu from "../../Common/Sidebar/SidebarMenu.vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import { translate } from "../../Common/translations";
    import urls from "../../Urls/urls";
    import history from "../../Common/history";

    const store = useFederationDashboardStore();

    const initDone = ref(false);

    onMounted(() => {
        initDone.value = false;
        return store
            .refreshFederationMetadata()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    });

    // PICKING SECTION

    const sidebarMenu = ref<ComponentExposed<typeof SidebarMenu> | null>(null);

    function openSidebarMenu() {
        if (sidebarMenu.value) {
            sidebarMenu.value.openMenu();
        }
    }

    const federationManagementStore = useFederationDashboardStore();

    const activeSection = computed(() => {
        return federationManagementStore.activeSection;
    });

    function activateSection(section: FederationMenuSection) {
        federationManagementStore.activeSection = section;
        sidebarMenu.value?.hideMenu(); // Close the menu when a section is activated
    }

    //Remembering active section in query params + loading
    const historyInstance = history.getInstance(window);

    watch(activeSection, () => {
        historyInstance.setQueryParam("section", activeSection.value);
    });

    onMounted(() => {
        const sectionFromQuery = historyInstance.getQueryParam("section");
        if (sectionFromQuery) {
            federationManagementStore.activeSection = sectionFromQuery as FederationMenuSection;
        } else {
            federationManagementStore.activeSection = "federationDetails"; // Default section
        }
    });
</script>

<style scoped></style>
